import { RateDto } from "arps_wasm";
import { ALL_PRODUCT_TYPES, ChartTypeLabels } from "constants/chart.constants";
import { EvaChart, EvaSeriesKind, TimeStep } from "constants/charts.enums";
import { DEFAULT_LINE_CHART_OPTIONS } from "entities/charts";
import { TYPEWELL_Z_INDEX } from "entities/charts/eva/translators/constants";
import { ICheckedForecast } from "store/features";
import { TypeWellSeriesData } from "types/charts/typeWellSeries";
import { toExpectedTypeWellBackendProductName } from "utils/arps/productMapping";

import { INormalizeBySetting } from "models";
import { EntityKind } from "models/entityKind";

import { ProductTypeEnum } from "components/arps/models/ArpsSegment";
import { ArpsSegmentToSegmentDto as arpsSegmentToSegmentDto } from "components/arps/utils/arpsUtils";
import {
  getTypeWellSeries,
  isTypeWellSeriesAllZeros
} from "components/arps/utils/typeWellUtils";

import {
  getProductType,
  productStringToProductTypeEnum,
  tryProductStringToProductTypeEnum
} from "../../../../utils";
import { getTypeWellDisplayName } from "./typeWellHelpers";

export interface NormalizeSettings extends INormalizeBySetting {
  display?: string;
  normalize: boolean;
  normalizeTypeWell: boolean;
  per: number;
  threshold: number;
  useMultilinearNormalization: boolean;
  lowerScalar: number;
  higherScalar: number;
}

/**
 * Build normalize settings object with unit conversions applied.
 */
export function buildNormalizeSettings(
  normalizeBySettings: INormalizeBySetting,
  useNormalizeBy: boolean,
  normalizeTypeWell: boolean,
  convertedPer: number,
  convertedThreshold: number
): NormalizeSettings {
  return {
    ...normalizeBySettings,
    display: normalizeBySettings?.display ?? normalizeBySettings?.displayName,
    normalize: useNormalizeBy,
    normalizeTypeWell,
    per: convertedPer,
    threshold: convertedThreshold,
    useMultilinearNormalization:
      normalizeBySettings?.useMultilinearNormalization ?? false,
    lowerScalar: normalizeBySettings?.lowerScalar ?? 0.0,
    higherScalar: normalizeBySettings?.higherScalar ?? 0.0
  };
}

/**
 * Well data structure for WASM normalization.
 * This is the format expected by the arps-wasm library.
 */
export interface WasmWellData {
  length: number;
  stage: number;
  stage_spacing: number;
  proppant: number;
  proppant_intensity: number;
  additional_data: unknown[];
}

/**
 * Build well data object for normalization.
 */
export function buildWellData(
  tw: ICheckedForecast,
  shouldNormalize: boolean
): WasmWellData {
  if (!shouldNormalize) {
    return {
      length: 0,
      stage: 0,
      stage_spacing: 0,
      proppant: 0,
      proppant_intensity: 0,
      additional_data: []
    };
  }

  return {
    length: tw.wellData?.HzLength ?? 0,
    stage: tw.wellData?.Stage ?? 0,
    stage_spacing: tw.wellData?.StageSpacing ?? 0,
    proppant: tw.wellData?.Proppant ?? 0,
    proppant_intensity: tw.wellData?.ProppantIntensity ?? 0,
    additional_data: tw.wellData?.AdditionalData ?? []
  };
}

/**
 * Build forecast constants with proper unit and product name formatting.
 */
export function buildForecastConstants(
  constants: ICheckedForecast["constants"]
): Array<{ unit: string; product: string }> {
  return (constants ?? [])
    .map((fconst) => {
      const productEnum = productStringToProductTypeEnum(fconst.product);
      const productString = ProductTypeEnum[productEnum];
      const convertProductStringForBackend =
        toExpectedTypeWellBackendProductName(productString);
      if (!fconst.unit) {
        return null;
      }
      return {
        ...fconst,
        unit: fconst.unit,
        product: convertProductStringForBackend
      };
    })
    .filter((x) => x != null) as Array<{ unit: string; product: string }>;
}

/**
 * Check if a series product matches the chart's selected product.
 */
export function isProductMatch(seriesProduct: string, productKey: string): boolean {
  const normalizedSeriesProduct = seriesProduct.toLowerCase();
  const normalizedProductKey = productKey.replace(/[\s+_]/g, "").toLowerCase();

  // Direct match
  if (normalizedSeriesProduct === normalizedProductKey) {
    return true;
  }

  // Total Fluid special case
  if (
    productKey === ALL_PRODUCT_TYPES.TotalFluid.key &&
    productKey.indexOf(seriesProduct) >= 0
  ) {
    return true;
  }

  // 1+WOR special case
  if (seriesProduct === "OnePlusWOR" && productKey === "1+WOR") {
    return true;
  }

  // Steam injection special case
  if (
    seriesProduct === "SteamInjection" &&
    productKey === ALL_PRODUCT_TYPES.SteamInj.key
  ) {
    return true;
  }

  return false;
}

/**
 * Get the product segment from grouped arps segments.
 */
export function getProductSegment(
  arpsGroupedByProduct: Record<string, unknown>,
  productKey: string,
  crossKindProductKey: string | undefined
): unknown {
  let selectedProductSegment = crossKindProductKey
    ? arpsGroupedByProduct[crossKindProductKey.toUpperCase()]
    : arpsGroupedByProduct[productKey.toUpperCase()];

  // Handle Oil/Condensate fallback
  if (
    selectedProductSegment == null &&
    (productKey === ALL_PRODUCT_TYPES.Oil.key ||
      crossKindProductKey === ALL_PRODUCT_TYPES.Oil.key)
  ) {
    selectedProductSegment =
      "COND." in arpsGroupedByProduct
        ? arpsGroupedByProduct["COND."]
        : arpsGroupedByProduct["CONDENSATE"];
  }

  return selectedProductSegment;
}

export interface DeclineSeriesParams {
  typeWell: ICheckedForecast;
  typeWellId: string;
  chartType: EvaChart;
  timeStep: TimeStep;
  yAxisTitle: string;
  seriesRateCum: RateDto[];
}

/**
 * Build a single decline chart line series for a type well.
 */
export function buildDeclineSeriesItem(
  params: DeclineSeriesParams
): TypeWellSeriesData[0] {
  const { typeWell, typeWellId, chartType, timeStep, yAxisTitle, seriesRateCum } = params;

  const typeWellName = getTypeWellDisplayName(typeWell);
  const seriesData = getTypeWellSeries(
    chartType as ChartTypeLabels,
    seriesRateCum,
    yAxisTitle,
    timeStep
  );

  return {
    ...DEFAULT_LINE_CHART_OPTIONS,
    name: typeWellName,
    id: `${typeWellId},${typeWell.id}`,
    originalId: typeWell.id,
    type: "line",
    kind: EvaSeriesKind.TypeWell,
    itemStyle: {
      opacity: 1,
      color: typeWell.color ?? "#000000"
    },
    z: TYPEWELL_Z_INDEX,
    showSymbol: false,
    lineStyle: {
      opacity: 1,
      type: [typeWell.thickness * 4, typeWell.thickness],
      width: typeWell.thickness
    },
    line: {
      originalColor: typeWell.color ?? "#000000",
      originalWidth: typeWell.thickness
    },
    data: seriesData
  } as TypeWellSeriesData[0];
}

export interface DeclineChartContext {
  arpsWasm: typeof import("components/arps/utils/wasmLoader").arpsWasm;
  chartType: EvaChart;
  chartProduct: string;
  timeStep: TimeStep;
  entityKind: EntityKind;
  typeWellId: string;
  yAxisTitle: string;
  forecastArrays: { forecastArray: Record<string, unknown[]> } | undefined;
  normalizeSettings: NormalizeSettings;
  useNormalizeBy: boolean;
}

export interface DeclineChartResult {
  series: TypeWellSeriesData;
  missingNormalizationCount: number;
}

/**
 * Process a single type well for decline chart series.
 * Returns the series items and whether normalization data was missing.
 */
export function processTypeWellForDeclineChart(
  tw: ICheckedForecast,
  context: DeclineChartContext
): { items: TypeWellSeriesData; missingCount: number } {
  const {
    arpsWasm,
    chartType,
    chartProduct,
    timeStep,
    entityKind,
    typeWellId,
    yAxisTitle,
    forecastArrays,
    normalizeSettings,
    useNormalizeBy
  } = context;

  const items: TypeWellSeriesData = [];
  let missingCount = 0;

  const product = getProductType(chartProduct, entityKind);

  // Check if we have data to process
  const hasForecastArray = forecastArrays?.forecastArray[tw.id]?.length > 0;

  if (!tw.arps?.length && !hasForecastArray) {
    return { items, missingCount };
  }

  const declineSegments = [...tw.arps];
  const dto = arpsSegmentToSegmentDto(arpsWasm, declineSegments);

  if (dto.length === 0 && !hasForecastArray) {
    return { items, missingCount };
  }

  const forecastConstantsWithUnit = buildForecastConstants(tw.constants);
  const wellData = buildWellData(tw, normalizeSettings.normalize);

  const settings = {
    isDaily: timeStep === TimeStep.Day,
    isCumTime: chartType === EvaChart.CumTime
  };

  // Derive the numeric ProductTypeEnum value for the active chart product so we
  // can ask the WASM to compute only that product's adder.  This skips all
  // unrelated product computations and is the key enabler for 30 fps drag
  // previews.  Fall back to the unfiltered call for unknown/unmapped products.
  //
  // Use product.key, not chartProduct: chartProduct is the display *label*
  // (e.g. "Oil + Cond"), which productStringToProductTypeEnum does not know --
  // it would warn and return Unknown, silently disabling this optimization for
  // the default chart product. getProductType resolves the label to its key.
  //
  // An unmapped product (Total Fluid, BOE, the injection products, ...) is an
  // expected, silently-handled miss here -- it just falls to the unfiltered
  // call below -- not a logged anomaly, so use the non-warning variant. This
  // runs once per type well per series rebuild, ~30 times/second during a
  // drag with several checked type wells; the warning path would otherwise
  // spam the console for every unmapped product on every rebuild.
  const productTypeId = tryProductStringToProductTypeEnum(product.key);
  const productIds = productTypeId !== undefined ? [productTypeId as number] : undefined;

  const seriesData = hasForecastArray
    ? arpsWasm.getForecastFromArray(
        forecastArrays.forecastArray[tw.id],
        normalizeSettings,
        wellData,
        {},
        settings
      )
    : productIds
    ? arpsWasm.getForecastFromArpsSegmentsForProducts(
        dto,
        forecastConstantsWithUnit,
        normalizeSettings,
        wellData,
        {},
        settings,
        productIds
      )
    : arpsWasm.getForecastFromArpsSegments(
        dto,
        forecastConstantsWithUnit,
        normalizeSettings,
        wellData,
        {},
        settings
      );

  for (const series of seriesData) {
    if (!isProductMatch(series.product, product.key)) {
      continue;
    }

    const isSeriesEmpty = isTypeWellSeriesAllZeros(series.rate_cum);

    if (isSeriesEmpty) {
      missingCount++;
      if (useNormalizeBy) {
        continue;
      }
    }

    items.push(
      buildDeclineSeriesItem({
        typeWell: tw,
        typeWellId,
        chartType,
        timeStep,
        yAxisTitle,
        seriesRateCum: series.rate_cum
      })
    );
  }

  return { items, missingCount };
}

/**
 * Check if the chart type is a valid decline chart (Rate or Cum Time, not Total).
 */
export function isValidDeclineChart(chartType: string | undefined): boolean {
  return (
    !!chartType &&
    (chartType.includes("Rate") || chartType.includes("Cum Time")) &&
    !chartType.includes("Total")
  );
}

/**
 * Reverse the data array for each series at render time.
 * Creates a shallow copy of the series objects to avoid mutating original data.
 * The data array within each series is reversed in-place for rendering.
 */
export function reverseTypeWellSeriesForRendering(
  series: TypeWellSeriesData
): TypeWellSeriesData {
  return series.map((item) => {
    const data = item.data ? Array.prototype.slice.call(item.data).reverse() : [];
    return {
      ...item,
      data
    };
  });
}
